import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { LOGO_URL } from "@/data/site";
import { useAuth } from "@/context/AuthContext";
import { User, CalendarDays, Menu, X } from "lucide-react";

const LINKS = [
  { href: "#come-funziona", label: "Come Funziona" },
  { href: "#vantaggi", label: "Vantaggi" },
  { href: "#pacchetti", label: "Pacchetti" },
  { href: "#galleria", label: "Galleria" },
  { href: "#faq", label: "FAQ" },
  { href: "#contatti", label: "Contatti" },
];

export function Nav({ onBook, onAuth }) {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled ? "glass border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link">
          <img src={LOGO_URL} alt="AirDroneJet" className="h-10 w-auto" />
          <span className="font-heading font-extrabold tracking-tight text-lg hidden sm:block">
            AIR<span className="fire-text">DRONE</span>JET
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="font-mono-adj text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-orange-400 transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {user?.role === "admin" && (
            <a
              href="/admin"
              data-testid="nav-admin-promo-link"
              className="hidden sm:flex items-center gap-2 font-mono-adj text-[11px] uppercase tracking-[0.15em] text-orange-400 border border-orange-500/40 rounded-full px-5 py-2.5 hover:bg-orange-500/10 transition-colors duration-300"
            >
              Promo
            </a>
          )}
          {user ? (
            <a
              href="/area-clienti"
              data-testid="nav-my-bookings-button"
              className="hidden sm:flex items-center gap-2 font-mono-adj text-[11px] uppercase tracking-[0.15em] text-slate-300 border border-white/15 rounded-full px-5 py-2.5 hover:border-orange-500/60 hover:text-orange-400 transition-colors duration-300"
            >
              <CalendarDays size={14} />
              Area Clienti
            </a>
          ) : (
            <button
              onClick={onAuth}
              data-testid="nav-login-button"
              className="hidden sm:flex items-center gap-2 font-mono-adj text-[11px] uppercase tracking-[0.15em] text-slate-300 border border-white/15 rounded-full px-5 py-2.5 hover:border-orange-500/60 hover:text-orange-400 transition-colors duration-300"
            >
              <User size={14} />
              Area Clienti
            </button>
          )}
          <button
            onClick={() => onBook(null)}
            data-testid="nav-book-now-button"
            className="fire-bg text-white font-mono-adj text-[11px] uppercase tracking-[0.15em] font-semibold rounded-full px-5 sm:px-6 py-2.5 hover:opacity-90 hover:scale-[1.03] active:scale-95 transition-transform duration-300"
          >
            Prenota Ora
          </button>
          <button
            className="lg:hidden text-slate-300 p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-button"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-white/10 px-6 py-5 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="font-mono-adj text-xs uppercase tracking-[0.2em] text-slate-300 hover:text-orange-400"
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={() => {
              setOpen(false);
              if (user) {
                window.location.href = "/area-clienti";
              } else {
                onAuth();
              }
            }}
            data-testid="nav-mobile-account-button"
            className="text-left font-mono-adj text-xs uppercase tracking-[0.2em] text-orange-400"
          >
            Area Clienti
          </button>
        </div>
      )}
    </motion.header>
  );
}
